import React, {
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { Loader } from '@playbookux/playbook-web-core';
import useDebounce from '../../../hooks/use-debounce';
import { FILTER_COLLECTION } from './graphql';
import {
  Button, Container,
} from '../text-filter-component/styles';
import {
  CheckboxContainer, Input, OptionsContainer, Label, Title,
} from './style';
import optionMap from './optionMap';

const MultiFilterComponent = (props) => {
  const { api, column, colDef } = props;

  const keyMap = {
    language: 'languages',
    country: 'country',
    state: 'state',
    ethnicity: 'race_ethnic_heritage',
    seniority: ['current_position', 'seniority'],
    industry: ['current_position', 'company', 'industry'],
    jobRole: 'job_categories',
    age: 'birthdate',
    gender: 'gender',
  };

  const [filterOptions, setFilterOptions] = useState(colDef.collection || []);
  const [debouncedfilterOptions] = useDebounce(() => filterOptions, 500, [filterOptions]);
  const key = JSON.stringify(keyMap[colDef.field]) || JSON.stringify([colDef.field]);
  const isSignupQuery = colDef.isSignupQuery || false;

  const [getCollection, { data = [], loading, error }] = useLazyQuery(FILTER_COLLECTION, {
    variables: {
      key,
      isSignupQuery,
      scope: colDef.scope,
    },
    onCompleted: ({ collection }) => {
      setFilterOptions(collection || []);
    },
  });

  const options = colDef.collection || data?.collection || [];

  const updateFilter = async () => {
    await api.setColumnFilterModel(column.colId, {
      type: colDef.type,
      value: debouncedfilterOptions,
    });
    api.onFilterChanged();
  };

  const onChange = (event, option) => {
    if (event.target.checked) {
      setFilterOptions([...filterOptions, option]);
    } else {
      setFilterOptions(filterOptions.filter((id) => id !== option));
    }
  };

  const onResetButtonClick = async () => {
    setFilterOptions(options);
    await api.setColumnFilterModel(column.colId, null);
    api.onFilterChanged();
  };

  useEffect(() => {
    if (!colDef.collection) {
      getCollection();
    }
  }, []);

  useEffect(() => {
    if (debouncedfilterOptions !== options) {
      updateFilter();
    }
  }, [debouncedfilterOptions, api]);

  const nonListedOptions = options ? filterOptions.filter((filter) => !options.map((value) => `${value}`).includes(`${filter}`)) : [];
  const composedOptions = [...nonListedOptions, ...options];

  return (
    <Container>
      <Title>{colDef.headerName}</Title>
      {(loading || error || !options) && (
        <Loader data-loader="multi-filter-loader" />
      )}
      {(!loading && !error) && options && (
        <>
          <OptionsContainer>
            <CheckboxContainer>
              <Input
                type="checkbox"
                checked={filterOptions.length === options.length}
                onChange={(event) => {
                  if (event.target.checked) {
                    onResetButtonClick();
                  } else {
                    setFilterOptions([]);
                  }
                }}
              />
              <Label>Select All</Label>
            </CheckboxContainer>
            {composedOptions.map((option) => (
              <CheckboxContainer key={option}>
                <Input
                  type="checkbox"
                  checked={filterOptions && filterOptions.map((value) => `${value}`).includes(`${option}`)}
                  onChange={(event) => onChange(event, option)}
                />
                <Label>{optionMap[option] || option}</Label>
              </CheckboxContainer>
            ))}
          </OptionsContainer>
          <Button onClick={onResetButtonClick}>Reset filter</Button>
        </>
      )}
    </Container>
  );
};

MultiFilterComponent.propTypes = {
  api: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
  colDef: PropTypes.shape({
    headerName: PropTypes.string,
    type: PropTypes.string,
    collection: PropTypes.arrayOf(PropTypes.string),
    isSignupQuery: PropTypes.bool,
    scope: PropTypes.string,
    field: PropTypes.string,
  }).isRequired,
};

export default MultiFilterComponent;
